import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Sparkles, Eye, EyeOff, ShieldCheck } from "lucide-react";
import "./employee.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeLogin() {
  const nav = useNavigate();
  const [dark, setDark] = useState(true);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("tr_employee_token")) nav("/employee/dashboard", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!identifier.trim() || !password) { toast.error("Enter Employee ID / Mobile and password"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/employee/auth/login`, { identifier: identifier.trim(), password });
      localStorage.setItem("tr_employee_token", data.token);
      localStorage.setItem("tr_employee", JSON.stringify(data.employee));
      toast.success(`Welcome, ${data.employee.name}`);
      nav("/employee/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`emp-scope ${dark ? "emp-dark" : ""} min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden`}>
      {/* Ambient glow bg */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(201,169,104,0.25), transparent)", filter: "blur(40px)" }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(30,44,90,0.35), transparent)", filter: "blur(60px)" }} />
      </div>

      <div className="relative z-10 w-full max-w-md emp-fade-in">
        <div className="emp-glass p-8 sm:p-10">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]" style={{ color: "var(--emp-muted)" }}>← TileRoom</Link>
            <button
              type="button"
              onClick={() => setDark(d => !d)}
              className="emp-btn-ghost text-[10px]"
              data-testid="emp-login-theme-toggle"
            >
              {dark ? "Light" : "Dark"}
            </button>
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex w-14 h-14 items-center justify-center border border-current mb-4" style={{ borderColor: "var(--emp-accent)", color: "var(--emp-accent)" }}>
              <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <p className="emp-eyebrow mb-2">Employee Portal</p>
            <h1 className="font-editorial text-3xl sm:text-4xl leading-tight mb-2">Welcome back</h1>
            <p className="text-sm" style={{ color: "var(--emp-muted)" }}>Sign in to view your ETS, today's tasks and leaderboard.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="emp-eyebrow block mb-2">Employee ID or Mobile</label>
              <input
                type="text"
                autoFocus
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="TR0001 or +91 98xxxxxxxx"
                className="emp-input"
                data-testid="emp-login-id"
              />
            </div>
            <div>
              <label className="emp-eyebrow block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="emp-input pr-10"
                  data-testid="emp-login-password"
                />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100">
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={busy} className="emp-btn-primary w-full inline-flex items-center justify-center gap-2 mt-2" data-testid="emp-login-submit">
              {busy ? "Signing in…" : (<><LogIn className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign In</>)}
            </button>
          </form>

          <p className="text-[11px] text-center mt-8" style={{ color: "var(--emp-muted)" }}>
            Only <strong>active</strong> employees can access this portal. If your account is inactive, suspended or resigned, please contact HR.
          </p>
        </div>

        <p className="mt-6 text-center emp-eyebrow" style={{ opacity: 0.6 }}>
          <Sparkles className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> TileRoom Intelligence · Employee Trust Score
        </p>
      </div>
    </div>
  );
}
